import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Tabs,
  Tab,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import PeopleIcon from '@mui/icons-material/People';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import axios from 'axios';
import { NotificationData, NOTIFICATION_TEMPLATES, getNotificationDisplayName } from '../../types/notification';
import { getPrefectureName } from '../../constants/prefectures';
import { UserDetailProfile } from '../../types/admin';
import { UserStatus } from '../../types/auth';
import { useEventDetail } from '../../hooks/useEventDetail';
import { EventData, LOCATION_TYPE_LABELS, INDUSTRY_LABELS, COMPANY_SCALE_LABELS } from '../../types/event';
import { CategoryBadge } from '../events/CategoryBadge';
import { DeadlineLabel } from '../events/DeadlineLabel';

const DAY_OF_WEEK = ['日', '月', '火', '水', '木', '金', '土'];

const formatEventDate = (dateStr: string): string => {
  const d = new Date(dateStr + 'T00:00:00');
  return `${d.getMonth() + 1}月${d.getDate()}日（${DAY_OF_WEEK[d.getDay()]}）`;
};

const formatTime = (t: string): string => t.slice(0, 5);

const formatDatetime = (iso: string): string => {
  const d = new Date(iso);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${y}/${mo}/${day} ${h}:${mi}`;
};

const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return '未設定';
  return new Date(dateString).toLocaleDateString('ja-JP');
};

const calculateAge = (birthDate: string | undefined): string => {
  if (!birthDate) return '';
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return ` (${age}歳)`;
};

interface InfoRowProps {
  icon?: React.ReactNode;
  label: string;
  value?: string | React.ReactNode;
  isEmpty?: boolean;
}

const InfoRow: React.FC<InfoRowProps> = ({ icon, label, value, isEmpty }) => (
  <Box display="flex" alignItems="flex-start" gap={1.5} mb={1.5}>
    {icon
      ? <Box sx={{ color: 'primary.main', mt: '2px', flexShrink: 0, fontSize: 18 }}>{icon}</Box>
      : <Box sx={{ width: 18, flexShrink: 0 }} />
    }
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" mb={0.25} fontWeight={500}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={500}
        color={isEmpty ? 'text.secondary' : 'text.primary'}
        fontStyle={isEmpty ? 'italic' : 'normal'}
      >
        {value ?? '未設定'}
      </Typography>
    </Box>
  </Box>
);

interface SectionProps {
  icon?: React.ReactNode;
  title: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ icon, title, children }) => (
  <Box mb={2.5}>
    <Box display="flex" alignItems="center" gap={1} mb={1.5}>
      {icon && <Box sx={{ color: 'primary.main' }}>{icon}</Box>}
      <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
    </Box>
    {children}
  </Box>
);

interface StudentTabProps {
  userId: number;
}

const GENDER_LABELS: Record<string, string> = {
  male:   '男性',
  female: '女性',
  other:  'その他',
};

const StudentTab: React.FC<StudentTabProps> = ({ userId }) => {
  const [profile, setProfile] = useState<UserDetailProfile | null>(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const timeoutId  = setTimeout(() => controller.abort(), 60000);

    setLoading(true);
    setError(null);

    axios
      .get(`/api/admin/users/${userId}/profile`, {
        signal:  controller.signal,
        timeout: 60000,
      })
      .then((res) => {
        clearTimeout(timeoutId);
        if (!cancelled) setProfile(res.data.data);
      })
      .catch((err) => {
        clearTimeout(timeoutId);
        if (cancelled) return;
        let msg = 'プロフィールの取得に失敗しました';
        if (axios.isAxiosError(err)) {
          if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
            msg = '読み込みがタイムアウトしました。再度お試しください。';
          } else if (err.response?.status === 404) {
            msg = 'ユーザーが見つかりません';
          } else {
            msg = err.response?.data?.error?.message || msg;
          }
        }
        setError(msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [userId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>;
  }

  if (!profile) return null;

  return (
    <Box>
      <Section title="申込者情報">
        {/* 名前 + ふりがなを1行に統合 */}
        <InfoRow
          icon={<PersonIcon sx={{ fontSize: 16 }} />}
          label="名前"
          value={
            profile.furigana
              ? `${profile.fullName}（${profile.furigana}）`
              : profile.fullName
          }
        />
        <InfoRow
          label="生年月日"
          value={profile.birthDate ? `${formatDate(profile.birthDate)}${calculateAge(profile.birthDate)}` : undefined}
          isEmpty={!profile.birthDate}
        />
        <InfoRow
          label="性別"
          value={profile.gender ? (GENDER_LABELS[profile.gender] ?? profile.gender) : undefined}
          isEmpty={!profile.gender}
        />
        <InfoRow icon={<PhoneIcon sx={{ fontSize: 16 }} />} label="電話番号" value={profile.phone}  isEmpty={!profile.phone} />
        <InfoRow icon={<EmailIcon sx={{ fontSize: 16 }} />} label="メール"   value={profile.email} />
        <InfoRow icon={<SchoolIcon sx={{ fontSize: 16 }} />} label="大学"     value={profile.university} isEmpty={!profile.university} />
        <InfoRow label="学部/学科" value={profile.faculty} isEmpty={!profile.faculty} />
        {profile.graduationYear && <InfoRow label="卒業年度" value={`${profile.graduationYear}年卒`} />}
        {profile.currentPrefecture && <InfoRow icon={<LocationOnIcon sx={{ fontSize: 16 }} />} label="居住地" value={getPrefectureName(profile.currentPrefecture) || profile.currentPrefecture} />}
      </Section>
    </Box>
  );
};

interface EventTabProps {
  eventId: number;
}

const EventTab: React.FC<EventTabProps> = ({ eventId }) => {
  const { event, isLoading, error, fetch, clear } = useEventDetail();

  useEffect(() => {
    fetch(eventId);
    return () => clear();
  }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>;
  }

  if (!event) return null;

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <CategoryBadge category={event.category} />
        {event.is_participated && (
          <Chip label="参加申込済み" size="small" color="primary" />
        )}
      </Stack>

      <Typography variant="h6" fontWeight={700}>{event.title}</Typography>

      <Stack direction="row" spacing={1} alignItems="flex-start">
        <BusinessIcon sx={{ color: 'text.secondary', fontSize: 18, mt: '2px', flexShrink: 0 }} />
        <Box>
          <Typography variant="body2">{event.company_name}</Typography>
          {(event.industry || event.company_scale) && (
            <Typography variant="caption" color="text.secondary">
              {[
                event.industry      && INDUSTRY_LABELS[event.industry],
                event.company_scale && COMPANY_SCALE_LABELS[event.company_scale],
              ].filter(Boolean).join('・')}
            </Typography>
          )}
        </Box>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center">
        <AccessTimeIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
        <Typography variant="body2">
          {formatEventDate(event.event_date)}　{formatTime(event.start_time)}〜{formatTime(event.end_time)}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center">
        <LocationOnIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
        <Typography variant="body2">
          {LOCATION_TYPE_LABELS[event.location_type]}　{event.location}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center">
        <PeopleIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
        <Typography variant="body2">
          定員 {event.capacity}名　/ 申込 {event.current_participants ?? '―'}名
        </Typography>
      </Stack>

      <Box>
        <DeadlineLabel event={event} />
      </Box>

      {event.description && (
        <>
          <Divider />
          <Typography variant="body2" whiteSpace="pre-wrap">
            {event.description}
          </Typography>
        </>
      )}
    </Stack>
  );
};

interface CompanyTabProps {
  company: {
    id:                 number;
    name:               string;
    prefecture:         string | null;
    city:               string | null;
    businessType:       string | null;
    employeeCountRange: string | null;
    contactEmail:       string;
  };
}

const CompanyTab: React.FC<CompanyTabProps> = ({ company }) => {
  const location = [company.prefecture, company.city].filter(Boolean).join(' ') || null;

  return (
    <Box>
      <Section title="企業情報">
        <InfoRow
          icon={<BusinessIcon sx={{ fontSize: 16 }} />}
          label="企業名"
          value={company.name}
        />
        <InfoRow
          icon={<LocationOnIcon sx={{ fontSize: 16 }} />}
          label="所在地"
          value={location ?? undefined}
          isEmpty={!location}
        />
        <InfoRow
          icon={<WorkIcon sx={{ fontSize: 16 }} />}
          label="事業種別"
          value={company.businessType ?? undefined}
          isEmpty={!company.businessType}
        />
        <InfoRow
          icon={<PeopleIcon sx={{ fontSize: 16 }} />}
          label="従業員規模"
          value={company.employeeCountRange ?? undefined}
          isEmpty={!company.employeeCountRange}
        />
        <InfoRow
          icon={<EmailIcon sx={{ fontSize: 16 }} />}
          label="連絡先メール"
          value={company.contactEmail}
        />
      </Section>
    </Box>
  );
};

interface NotificationDetailDialogProps {
  open: boolean;
  onClose: () => void;
  notification: NotificationData | null;
  onToggleHandled?: (ntf: NotificationData) => void;
}

export const NotificationDetailDialog: React.FC<NotificationDetailDialogProps> = ({
  open,
  onClose,
  notification,
  onToggleHandled,
}) => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (open) setTab(0);
  }, [open, notification?.id]);

  if (!notification) return null;

  const isEventCreated = notification.type === 'event_created';
  const isApplication  = notification.type === 'application';

  const summaryBgColor    = isEventCreated ? 'success.50'  : isApplication ? 'primary.50'  : 'error.50';
  const summaryBorderColor = isEventCreated ? 'success.100' : isApplication ? 'primary.100' : 'error.100';
  const dialogTitle        = isEventCreated ? 'イベント作成通知' : '申込詳細';
  const firstTabLabel      = isEventCreated ? '企業情報' : '学生情報';
  const FirstTabIcon       = isEventCreated ? BusinessIcon : PersonIcon;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'flex-start',
          paddingTop: '5%',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Typography variant="h6" fontWeight={700}>{dialogTitle}</Typography>
          <IconButton size="small" onClick={onClose} sx={{ ml: 1, flexShrink: 0 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* 通知サマリー */}
      <Box
        sx={{
          mx:          2.5,
          mb:          1.5,
          px:          2,
          py:          1.25,
          bgcolor:     summaryBgColor,
          border:      '1px solid',
          borderColor: summaryBorderColor,
          borderRadius: 1.5,
        }}
      >
        <Typography variant="caption" color="text.secondary" display="block" mb={0.25}>
          {formatDatetime(notification.created_at)}
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          {NOTIFICATION_TEMPLATES[notification.type](getNotificationDisplayName(notification), notification.event.title)}
        </Typography>
      </Box>

      {/* タブ */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2.5 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
          <Tab
            icon={<FirstTabIcon sx={{ fontSize: 18 }} />}
            iconPosition="top"
            label={firstTabLabel}
            sx={{ fontWeight: 600, minHeight: 64, fontSize: '0.8rem', py: 1 }}
          />
          <Tab
            icon={<EventIcon sx={{ fontSize: 18 }} />}
            iconPosition="top"
            label="イベント詳細"
            sx={{ fontWeight: 600, minHeight: 64, fontSize: '0.8rem', py: 1 }}
          />
        </Tabs>
      </Box>

      <DialogContent sx={{ pt: 2.5, px: 2.5, minHeight: 320 }}>
        {tab === 0 && (
          isEventCreated
            ? notification.company
              ? <CompanyTab company={notification.company} />
              : <Typography color="text.secondary">企業情報がありません</Typography>
            : notification.student
              ? <StudentTab userId={notification.student.id} />
              : <Typography color="text.secondary">学生情報がありません</Typography>
        )}
        {tab === 1 && <EventTab eventId={notification.event.id} />}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        {onToggleHandled && (
          <Button
            variant="outlined"
            color={notification.is_handled ? 'inherit' : 'success'}
            onClick={() => onToggleHandled(notification)}
            sx={{ mr: 'auto' }}
          >
            {notification.is_handled ? '対応済みを取消' : '✓ 対応済みにする'}
          </Button>
        )}
        <Button onClick={onClose} variant="outlined">閉じる</Button>
      </DialogActions>
    </Dialog>
  );
};
